import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, ArrowRight, Package, FileText, Share2, ShoppingCart, BarChart3, Code,
  CheckCircle2, AlertTriangle, Info, Lightbulb, ChevronRight, Rocket,
  Upload, Image, Settings, Eye, Globe, Copy, ExternalLink, RefreshCw, XCircle,
  Calendar, Filter, TrendingUp, Users, Download, Mail, CreditCard, MousePointer
} from 'lucide-react';

const TUTORIALS = {
  'create-product': {
    number: 1,
    title: 'Create Your First Product',
    subtitle: 'Learn how to upload and configure digital products for sale',
    icon: Package,
    color: 'bg-blue-100 text-blue-600',
    prev: null,
    next: 'build-landing-page',
    nextLabel: 'Build Your Landing Page',
    sections: [
      {
        type: 'overview',
        title: 'What You Will Learn',
        content: 'This guide walks you through the complete product creation flow — from choosing a product type to configuring download limits and publishing your product for sale.'
      },
      {
        type: 'steps',
        title: 'Step 1: Go to Products',
        content: 'From your dashboard sidebar, click on **Products** under the "Manage" section. Then click the **"+ Add Product"** button in the top right corner to begin.',
        icon: Package,
      },
      {
        type: 'steps',
        title: 'Step 2: Choose Product Type',
        content: 'Select the type that best describes your product. This helps organize your catalog and appears as a label on your storefront.',
        icon: MousePointer,
        details: [
          { label: 'Ebook', desc: 'PDF, EPUB, or any digital book format. Best for guides, manuals, and written content.' },
          { label: 'Course', desc: 'Video courses and educational tutorials. Can be ZIP files containing multiple lesson files.' },
          { label: 'Template', desc: 'Design files, Canva templates, spreadsheet templates, or document templates.' },
          { label: 'Preset', desc: 'Lightroom presets, video LUTs, or filter packs for photo/video editing.' },
          { label: 'Poster', desc: 'Digital posters, wall art, or printable designs.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 3: Fill in Product Details',
        content: 'Enter the essential information about your product.',
        icon: FileText,
        details: [
          { label: 'Product Name', desc: 'A clear, descriptive name. This is what customers see on your storefront. Example: "Instagram Marketing Guide 2024"' },
          { label: 'Description', desc: 'Up to 500 characters. Describe what the customer will receive. Highlight key benefits, what\'s included, and who it\'s for.' },
          { label: 'Price (RM)', desc: 'Set your price in Malaysian Ringgit (MYR). Set to RM 0.00 for a free product. Tip: Price based on the value you provide, not just effort.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 4: Upload Your Files',
        content: 'Upload the actual digital product file and a cover image.',
        icon: Upload,
        details: [
          { label: 'Product File', desc: 'The main file customers will download after purchase. Supported: PDF, ZIP, MP4, and other formats up to 100MB. For multiple files, ZIP them together.' },
          { label: 'Cover Image (Thumbnail)', desc: 'The product preview image shown on your storefront. JPG or PNG, max 5MB. Use a professional-looking cover — it directly impacts sales.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 5: Configure Settings',
        content: 'Set download limits and expiry rules to protect your digital product.',
        icon: Settings,
        details: [
          { label: 'Max Downloads Per Customer', desc: 'How many times each customer can download the file. Default is 5. Set lower (e.g., 3) for premium content. Once the limit is reached, the customer must request a new link.' },
          { label: 'Download Link Expiry', desc: 'How long the download link stays active after purchase. Options: 24 hours, 2 days, 3 days (default), or 7 days. After expiry, the customer cannot download even if they haven\'t reached the download limit.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 6: Save or Publish',
        content: 'Review the product summary at the bottom, then choose your action.',
        icon: CheckCircle2,
        details: [
          { label: 'Save as Draft', desc: 'Saves the product but does NOT make it visible on any landing page. Use this when you\'re still preparing.' },
          { label: 'Publish Product', desc: 'Saves and makes the product available. You can then add it to your landing pages.' },
        ]
      },
      {
        type: 'warning',
        title: 'Important Notes',
        items: [
          'You can edit product details anytime after creation — name, price, description, and settings can all be updated.',
          'Changing the price does NOT affect existing orders. Customers who already paid the old price keep their access.',
          'If you upload a new file to replace the old one, existing download links will serve the NEW file.',
          'Products with RM 0.00 price are free — customers still need to enter their name and email to "purchase" them.',
          'Deleting a product will NOT revoke existing download links. To stop access, revoke individual orders instead.',
        ]
      },
      {
        type: 'tip',
        title: 'Pro Tips',
        items: [
          'Use a high-quality cover image — products with thumbnails convert 3x better than those without.',
          'Start with a lower price and increase as you get reviews and social proof.',
          'Bundle related products into a ZIP file and sell as a package at a higher price.',
          'Set download limits to 3-5 for most products. This prevents excessive sharing while being fair to customers.',
        ]
      }
    ]
  },
  'build-landing-page': {
    number: 2,
    title: 'Build Your Landing Page',
    subtitle: 'Create a professional storefront to showcase and sell your products',
    icon: FileText,
    color: 'bg-teal-100 text-teal-600',
    prev: 'create-product',
    next: 'publish-share',
    nextLabel: 'Publish & Share Your Link',
    sections: [
      {
        type: 'overview',
        title: 'What You Will Learn',
        content: 'This guide covers the entire landing page creation process — from choosing an editor type and template, to customizing every detail of your page including colors, sections, product layouts, social links, and tracking pixels.'
      },
      {
        type: 'steps',
        title: 'Step 1: Create a New Landing Page',
        content: 'Go to **Landing Pages** in the sidebar, then click **"+ New Page"**. You\'ll be asked to choose an editor type first.',
        icon: FileText,
      },
      {
        type: 'steps',
        title: 'Step 2: Choose Your Editor Type',
        content: 'Funnely offers two editor modes. Choose the one that fits your needs.',
        icon: MousePointer,
        details: [
          { label: 'Template Editor', desc: 'Start with a pre-designed template (Creator Hub, Bold Seller, Warm Glow, etc.). Great for beginners — pick a design, customize text and colors, and you\'re done. 8 templates available.' },
          { label: 'Custom Editor', desc: 'Full control over your page. Add and arrange sections manually — headlines, text blocks, images, testimonials, FAQ, and more. Best for advanced users who want unique layouts.' },
          { label: 'AI Builder (Coming Soon)', desc: 'AI will generate the design and copy for you automatically. Not yet available.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 3: Pick a Template (Template Mode)',
        content: 'If you chose Template mode, you\'ll see a gallery of 8 ready-made templates. Each has a different visual style:',
        icon: Eye,
        details: [
          { label: 'Creator Hub', desc: 'Clean & minimal, teal theme — best for general content creators.' },
          { label: 'Bold Seller', desc: 'High contrast, dark text — conversion-focused for serious sellers.' },
          { label: 'Warm Glow', desc: 'Amber/warm tones — great for lifestyle and creative niches.' },
          { label: 'Neon Minimal', desc: 'Dark background with green accents — perfect for tech creators.' },
          { label: 'Rose Studio', desc: 'Pink/elegant — ideal for beauty, fashion, and feminine brands.' },
          { label: 'Ocean Pro', desc: 'Blue/professional — suited for B2B and educational content.' },
          { label: 'Sunset Vibe', desc: 'Orange/warm — lifestyle and wellness niches.' },
          { label: 'Midnight', desc: 'Dark with purple accents — sleek and modern aesthetic.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 4: Customize Your Page',
        content: 'Once in the editor, you can customize everything. The editor has a left panel (controls) and a right panel (live preview).',
        icon: Settings,
        details: [
          { label: 'Page Title & Slug', desc: 'The title appears at the top of your page. The slug determines your URL: /store/your-slug. Choose a short, memorable slug.' },
          { label: 'Headline & Subheadline', desc: 'The main text visitors see first. Keep headlines short and benefit-focused. Example: "Level Up Your Photography"' },
          { label: 'Bio', desc: 'A short introduction about you or your brand. Keep it under 200 characters.' },
          { label: 'Colors', desc: 'Primary Color (buttons, accents), Background Color, and Text Color. Stay consistent with your brand.' },
          { label: 'Avatar/Logo', desc: 'Upload a profile picture or logo that represents your brand.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 5: Add Products to Your Page',
        content: 'Select which products to display on this landing page.',
        icon: Package,
        details: [
          { label: 'Product Selection', desc: 'Check the products you want to feature. Only published products appear in the list.' },
          { label: 'Product Layout', desc: 'Choose how products are displayed: List (vertical), Card (grid), or Carousel (swipeable). Card layout works best for 2-4 products.' },
          { label: 'CTA Style', desc: 'Choose between "Buy Now" button per product or checkbox selection with a single checkout button.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 6: Add Body Sections (Custom Editor)',
        content: 'In Custom mode, you can add various content sections to your page. Use the "Add Section" button to insert:',
        icon: FileText,
        details: [
          { label: 'Headline Section', desc: 'Large text for section headers. Choose font, size, color, and alignment.' },
          { label: 'Text Section', desc: 'Paragraph text for descriptions and explanations.' },
          { label: 'Image Section', desc: 'Upload images to showcase your products or brand visuals.' },
          { label: 'Testimonial Section', desc: 'Add customer reviews with name, avatar, and rating. Social proof boosts conversion.' },
          { label: 'FAQ Section', desc: 'Common questions and answers. Reduces support requests and builds trust.' },
          { label: 'Divider', desc: 'Visual separator between sections.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 7: Social Links & Footer',
        content: 'Add your social media links and configure the page footer.',
        icon: Share2,
        details: [
          { label: 'Social Links', desc: 'Add links to Instagram, TikTok, Twitter/X, Facebook, YouTube, Threads, and WhatsApp. These appear as icons on your page.' },
          { label: 'Footer Text', desc: 'Custom footer text like copyright notice or contact info.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 8: Tracking Pixels (Optional)',
        content: 'Add tracking codes to monitor advertising performance.',
        icon: BarChart3,
        details: [
          { label: 'Facebook Pixel ID', desc: 'Enter your Facebook Pixel ID to track page views and conversions from Facebook/Instagram ads. You\'ll find this in your Meta Events Manager.' },
          { label: 'TikTok Pixel ID', desc: 'Enter your TikTok Pixel ID for ad tracking. Available in your TikTok Ads Manager.' },
        ]
      },
      {
        type: 'warning',
        title: 'Important Notes',
        items: [
          'The Live Preview panel shows exactly how your page will look when published. Always check it before publishing.',
          'You can switch between Template and Custom editor, but switching may reset some section configurations.',
          'The slug (URL) can be changed anytime, but changing it will break any previously shared links.',
          'Pages can have multiple products — customers can select and purchase multiple items in one checkout.',
          'Drag and drop sections to reorder them. The order in the editor = the order on your live page.',
          'Only published products can be added to landing pages. Draft products won\'t appear in the product selector.',
        ]
      },
      {
        type: 'tip',
        title: 'Pro Tips',
        items: [
          'Start with a template and customize from there — it\'s faster than building from scratch.',
          'Keep your landing page focused. 2-5 products is ideal. Too many products overwhelm visitors.',
          'Add at least one testimonial if you have customer feedback. Social proof is the #1 conversion booster.',
          'Use the FAQ section to address common objections: "Is it worth the price?", "What format is the file?", "Can I get a refund?"',
          'Test your page on mobile — most of your social media traffic will come from mobile devices.',
        ]
      }
    ]
  },
  'publish-share': {
    number: 3,
    title: 'Publish & Share Your Link',
    subtitle: 'Get your store live and start driving traffic',
    icon: Share2,
    color: 'bg-amber-100 text-amber-600',
    prev: 'build-landing-page',
    next: 'manage-orders',
    nextLabel: 'Manage Orders & Customers',
    sections: [
      {
        type: 'overview',
        title: 'What You Will Learn',
        content: 'This guide explains how to publish your landing page, get the shareable URL, and effectively distribute it across different platforms to reach your audience.'
      },
      {
        type: 'steps',
        title: 'Step 1: Publish Your Page',
        content: 'There are two ways to publish:',
        icon: Globe,
        details: [
          { label: 'From the Editor', desc: 'Click the "Publish" button at the top of the Landing Page Editor. Your page goes live immediately.' },
          { label: 'From the Landing Pages List', desc: 'On the Landing Pages page, find your draft page and click the "Publish" button on the card.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 2: Get Your Store URL',
        content: 'Once published, your page is accessible at a unique URL.',
        icon: ExternalLink,
        details: [
          { label: 'URL Format', desc: 'Your page URL follows this format: https://yourdomain.com/store/your-slug' },
          { label: 'Copy Link', desc: 'On the Landing Pages list, click the three-dot menu (⋮) on your page card and select "Copy Link". The URL is copied to your clipboard.' },
          { label: 'View Live', desc: 'Click "View" on the page card to open your published page in a new tab.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 3: Share on Social Media',
        content: 'Now distribute your link where your audience is.',
        icon: Share2,
        details: [
          { label: 'Instagram Bio', desc: 'Paste your Funnely link in your Instagram bio. This is the #1 traffic source for most creators. Consider using a link shortener like bit.ly for a cleaner look.' },
          { label: 'TikTok Bio', desc: 'Add the link to your TikTok profile. Mention it in your videos: "Link in bio to grab the template!"' },
          { label: 'Twitter/X', desc: 'Pin a tweet with your store link. Include a compelling description and product preview image.' },
          { label: 'Facebook', desc: 'Share in relevant groups (where allowed) or on your page/profile.' },
          { label: 'WhatsApp', desc: 'Send to close contacts, broadcast lists, or WhatsApp groups. Personal recommendations convert well.' },
          { label: 'Telegram', desc: 'Share in your Telegram channels or groups.' },
          { label: 'Email', desc: 'Include the link in your newsletter or email signature.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 4: Update Without Breaking Links',
        content: 'You can update your page content anytime without changing the URL.',
        icon: RefreshCw,
        details: [
          { label: 'Edit Anytime', desc: 'Go to Landing Pages → click Edit on your page. Change text, add products, update colors — save and it\'s live instantly.' },
          { label: 'URL Stays the Same', desc: 'As long as you don\'t change the slug, all existing shared links continue to work.' },
          { label: 'Change the Slug', desc: 'If you change the slug, the old URL will stop working. You\'ll need to update all shared links. Avoid changing slugs after sharing.' },
        ]
      },
      {
        type: 'warning',
        title: 'Important Notes',
        items: [
          'Draft pages are NOT accessible to the public. You must publish before sharing.',
          'If your page shows "Draft" badge, it means the URL won\'t load for visitors. Publish it first.',
          'Changing the slug after sharing will break all old links. Choose your slug carefully before publishing.',
          'You can unpublish a page by deleting it, but existing orders and download links remain active.',
          'Each landing page has its own unique URL. You can create multiple pages for different products or audiences.',
        ]
      },
      {
        type: 'tip',
        title: 'Sharing Best Practices',
        items: [
          'Create a sense of urgency: "Limited time — get 50% off my preset pack!"',
          'Include social proof in your posts: "500+ creators already using this template"',
          'Use story/reel content to showcase your product, then direct to the link.',
          'Track which platform drives the most sales using the Sales Report page view analytics.',
          'Consider creating separate landing pages for different audiences or campaigns to track performance individually.',
        ]
      }
    ]
  },
  'manage-orders': {
    number: 4,
    title: 'Manage Orders & Customers',
    subtitle: 'Track purchases, manage download access, and support your customers',
    icon: ShoppingCart,
    color: 'bg-emerald-100 text-emerald-600',
    prev: 'publish-share',
    next: 'track-performance',
    nextLabel: 'Track Your Performance',
    sections: [
      {
        type: 'overview',
        title: 'What You Will Learn',
        content: 'This guide covers how to view and manage orders, understand download status, reissue links for customers, revoke access when needed, and view customer purchase history.'
      },
      {
        type: 'steps',
        title: 'Step 1: View Your Orders',
        content: 'Go to **Orders** in the sidebar under "Audience". You\'ll see a table with all orders.',
        icon: ShoppingCart,
        details: [
          { label: 'Order Ref', desc: 'A unique reference code for each order. Customers receive this in their confirmation. Useful for support conversations.' },
          { label: 'Customer', desc: 'The customer\'s name and email address they entered during checkout.' },
          { label: 'Product', desc: 'Which product was purchased, along with the product type badge.' },
          { label: 'Amount', desc: 'The price paid in MYR (Malaysian Ringgit).' },
          { label: 'Status', desc: 'Order status — "paid" means payment was successful. Other statuses may include "pending" or "cancelled".' },
          { label: 'Downloads', desc: 'Shows usage like "2/5" meaning 2 out of 5 allowed downloads used. Also shows "Expired" or "Revoked" badges.' },
          { label: 'Date', desc: 'When the order was placed.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 2: Search & Filter Orders',
        content: 'Use the search bar at the top to find orders by customer name, product name, or order reference code. Type any keyword and results filter in real-time.',
        icon: Filter,
      },
      {
        type: 'steps',
        title: 'Step 3: Reissue a Download Link',
        content: 'If a customer\'s download link has expired or they\'ve used all their downloads, you can issue a new one.',
        icon: RefreshCw,
        details: [
          { label: 'How to Reissue', desc: 'Click the three-dot menu (⋮) on the order row, then select "Reissue Link". A brand new download link is created with fresh download count and expiry timer.' },
          { label: 'When to Reissue', desc: 'Customer says link expired, customer reached download limit, customer lost the download email, or after resolving a support issue.' },
          { label: 'What Happens', desc: 'A new download token is generated. The old link stops working. The customer needs the new link (share it via email or DM).' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 4: Revoke Download Access',
        content: 'If you need to stop a customer from downloading, you can revoke their access.',
        icon: XCircle,
        details: [
          { label: 'How to Revoke', desc: 'Click the three-dot menu (⋮) on the order row, then select "Revoke Access". Confirm the action.' },
          { label: 'When to Revoke', desc: 'Refund requests — revoke access after refunding. Suspected abuse — customer sharing links with others. Fraudulent purchase — unauthorized payment.' },
          { label: 'What Happens', desc: 'The download link is immediately disabled. The customer will see a "revoked" message if they try to download. The order status shows "Revoked" badge.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 5: View Customer Profiles',
        content: 'Go to **Customers** in the sidebar. Click on any customer to see their full profile.',
        icon: Users,
        details: [
          { label: 'Customer List', desc: 'Shows all unique customers who have purchased from you, with their name, email, total orders, and total spent.' },
          { label: 'Customer Detail Page', desc: 'Click a customer to see: purchase history (all orders), download status per order, contact info, and first/last purchase dates.' },
          { label: 'Search Customers', desc: 'Use the search bar to find customers by name or email.' },
        ]
      },
      {
        type: 'warning',
        title: 'Important Notes',
        items: [
          'Reissuing a link creates a NEW link — the old one stops working. Make sure to send the new link to the customer.',
          'Revoking access is immediate and cannot be undone. If you need to restore access, reissue a new link.',
          'One customer can have multiple orders. Each order has its own download link and independent limits.',
          'Free products (RM 0.00) still appear as orders with "paid" status. This is normal.',
          'Download count shows "2/5" format. Once 5/5 is reached, the customer cannot download anymore unless you reissue.',
          'Expired links show an "Expired" badge. The customer can ask you to reissue a new link.',
        ]
      },
      {
        type: 'tip',
        title: 'Customer Support Tips',
        items: [
          'Respond to reissue requests quickly — good customer service leads to repeat buyers.',
          'If multiple customers report expired links, consider increasing the default expiry duration in your product settings.',
          'Keep a note of order refs when communicating with customers. It makes finding orders much faster.',
          'Consider setting generous download limits (5-10) to reduce support requests while still protecting your content.',
        ]
      }
    ]
  },
  'track-performance': {
    number: 5,
    title: 'Track Your Performance',
    subtitle: 'Understand your sales data, visitor analytics, and conversion rates',
    icon: BarChart3,
    color: 'bg-rose-100 text-rose-600',
    prev: 'manage-orders',
    next: 'embed-checkout',
    nextLabel: 'Embed Checkout Forms',
    sections: [
      {
        type: 'overview',
        title: 'What You Will Learn',
        content: 'This guide explains how to use the Dashboard Overview and Sales Report to track revenue, monitor visitor analytics, identify top products, and use date range filters to analyze specific time periods.'
      },
      {
        type: 'steps',
        title: 'Step 1: Dashboard Overview',
        content: 'The **Overview** page (your dashboard home) gives you a quick snapshot of your store performance.',
        icon: BarChart3,
        details: [
          { label: 'Total Revenue', desc: 'Total amount earned from paid orders in the selected date range.' },
          { label: 'Total Orders', desc: 'Number of completed orders in the selected date range.' },
          { label: 'Customers', desc: 'Number of unique customers in the selected date range.' },
          { label: 'Products', desc: 'Number of products in your catalog (not filtered by date).' },
          { label: 'Revenue Trend Chart', desc: 'A visual chart showing daily revenue over time. Helps identify trends and peak sales days.' },
          { label: 'Top Products', desc: 'Your best-selling products ranked by total sales.' },
          { label: 'Recent Orders', desc: 'The 5 most recent orders with customer name, product, amount, and status.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 2: Use Date Range Filters',
        content: 'Both Overview and Sales Report have a date range selector in the top-right corner.',
        icon: Calendar,
        details: [
          { label: 'Today', desc: 'Shows only today\'s data. Use to check daily progress.' },
          { label: 'Yesterday', desc: 'Shows yesterday\'s data. Good for comparing day-over-day.' },
          { label: 'Last 7 Days', desc: 'Shows the past week. Default for weekly reviews.' },
          { label: 'Last 30 Days', desc: 'Shows the past month. The default view when you open the page.' },
          { label: 'This Month', desc: 'From the 1st of the current month to today.' },
          { label: 'Last Month', desc: 'The entire previous month. Great for monthly reports.' },
          { label: 'All Time', desc: 'No date filter — shows all historical data.' },
          { label: 'Custom Range', desc: 'Opens a calendar where you can pick any start and end date. Click the start date, then click the end date.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 3: Sales Report Deep Dive',
        content: 'Go to **Sales Report** in the sidebar under "Insights" for detailed analytics.',
        icon: TrendingUp,
        details: [
          { label: 'Revenue KPIs', desc: 'Total Revenue, Total Orders, Total Customers, and Average Order Value — all filtered by your selected date range.' },
          { label: 'Visitor KPIs', desc: 'Total Page Views, Unique Visitors, and Overall Conversion Rate. Conversion = (orders / page views) * 100.' },
          { label: 'Revenue Trend Chart', desc: 'Daily revenue chart for the selected period. Hover over data points to see exact amounts.' },
          { label: 'Sales by Product Type', desc: 'Bar chart showing which product types (Ebook, Course, Template, etc.) sell the most. Helps you focus on what works.' },
          { label: 'Conversion by Landing Page', desc: 'Table showing each landing page\'s views, unique visitors, orders, and conversion rate. Shows which pages convert best.' },
          { label: 'Top Selling Products', desc: 'Ranked list of your best products by revenue, with progress bars and sales counts.' },
        ]
      },
      {
        type: 'warning',
        title: 'Important Notes',
        items: [
          'Revenue only counts "paid" orders. Pending or cancelled orders are excluded from revenue calculations.',
          'Page views are tracked automatically when someone visits your published landing page. No extra setup needed.',
          'Unique visitors are tracked by a visitor ID cookie. The same person visiting twice counts as 1 unique visitor but 2 page views.',
          'Conversion rate by landing page uses the products attached to that page. If a product is on multiple pages, orders are counted for each page that lists it.',
          'The date range filter affects KPIs, charts, and product rankings. "All Time" removes the filter entirely.',
          'If you see 0% conversion rate, it means the page has views but no orders yet. This is normal for new pages.',
        ]
      },
      {
        type: 'tip',
        title: 'Analytics Tips',
        items: [
          'Check your Sales Report weekly using "Last 7 Days" to spot trends early.',
          'Compare "This Month" vs "Last Month" to see if you\'re growing.',
          'If a landing page has high views but low conversion, improve the page design or product pricing.',
          'If a product has high sales, consider creating similar products in the same niche.',
          'Use "Today" filter after launching a new promotion to track immediate impact.',
        ]
      }
    ]
  },
  'embed-checkout': {
    number: 6,
    title: 'Embed Checkout Forms',
    subtitle: 'Sell from your own website or blog with embeddable checkout forms',
    icon: Code,
    color: 'bg-violet-100 text-violet-600',
    prev: 'track-performance',
    next: null,
    nextLabel: null,
    sections: [
      {
        type: 'overview',
        title: 'What You Will Learn',
        content: 'This guide covers how to create embeddable checkout forms, customize them, and add them to any external website using a simple 3-line JavaScript snippet. This is optional — you can sell purely through your Funnely landing pages.'
      },
      {
        type: 'steps',
        title: 'Step 1: Go to Checkout Forms',
        content: 'From your dashboard sidebar, click on **Checkout Forms** under "Manage". Click **"+ New Form"** to create your first form.',
        icon: Code,
      },
      {
        type: 'steps',
        title: 'Step 2: Configure Your Form',
        content: 'Set up the checkout form with your preferences.',
        icon: Settings,
        details: [
          { label: 'Form Name', desc: 'An internal name for your reference (not shown to customers). Example: "Blog Sidebar Checkout" or "Main Product Form".' },
          { label: 'Select Products', desc: 'Choose which products to include in this form. You can include multiple products — customers can select which ones they want.' },
          { label: 'Form Fields', desc: 'Choose which fields to collect: Name and Email are always required. Optional: Phone Number, Address, Order Notes.' },
          { label: 'Button Text', desc: 'Customize the checkout button text. Default: "Buy Now". Examples: "Get Access", "Download Now", "Purchase".' },
          { label: 'Button Color', desc: 'Choose a color that matches your website\'s design.' },
          { label: 'Payment Methods', desc: 'Select which payment options to show: FPX Online Banking, Credit/Debit Card, E-Wallet (TnG, Boost, GrabPay).' },
          { label: 'Success Message', desc: 'The message customers see after successful purchase. Default: "Thank you! Check your email for the download link."' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 3: Get the Embed Code',
        content: 'After saving your form, click **"Get Code"** (or the code icon) on the form card.',
        icon: Copy,
        details: [
          { label: 'The Embed Snippet', desc: 'You\'ll get a 3-line JavaScript snippet. It looks like: <div id="funnely-checkout-{form_id}"></div> followed by a <script> tag.' },
          { label: 'Copy Code', desc: 'Click the "Copy" button to copy the snippet to your clipboard.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 4: Add to Your Website',
        content: 'Paste the code into your external website where you want the checkout form to appear.',
        icon: Code,
        details: [
          { label: 'WordPress', desc: 'Add a "Custom HTML" block in your page/post editor, paste the snippet, and publish.' },
          { label: 'Blogger/Blogspot', desc: 'Switch to HTML view in your post editor, paste the snippet where you want the form.' },
          { label: 'Wix', desc: 'Use the "Embed HTML" element from the Add menu. Paste the snippet inside.' },
          { label: 'Any HTML Site', desc: 'Paste the snippet directly into your HTML file where you want the form to render.' },
          { label: 'Shopify/Other', desc: 'Most platforms have a "Custom HTML" or "Embed Code" section. Paste the snippet there.' },
        ]
      },
      {
        type: 'steps',
        title: 'Step 5: Test the Form',
        content: 'After embedding, visit your external website and verify the form renders correctly.',
        icon: Eye,
        details: [
          { label: 'Check Display', desc: 'The form should show your products, customer fields, and the purchase button.' },
          { label: 'Test Purchase', desc: 'Try a test purchase to make sure the entire flow works — form submission, payment (in test mode, it auto-completes), and download link delivery.' },
          { label: 'Mobile Test', desc: 'Check how the form looks on mobile devices. It should be responsive.' },
        ]
      },
      {
        type: 'warning',
        title: 'Important Notes',
        items: [
          'The embed code uses JavaScript to render the form. The external site must allow JavaScript for it to work.',
          'Orders from embedded forms appear in your Orders page just like landing page orders.',
          'If you update the form (change products, fields, etc.), the changes apply automatically on all sites using that embed code — no need to re-paste.',
          'Deleting a checkout form will cause the embed to stop rendering on external sites.',
          'Each form has its own unique ID. You can have multiple different forms for different purposes.',
          'The embed form inherits the Funnely checkout styling, not your external site\'s styling.',
        ]
      },
      {
        type: 'tip',
        title: 'Embed Strategy Tips',
        items: [
          'Place the checkout form near the bottom of a blog post that discusses the product — high intent readers will convert.',
          'Create different forms for different products. A blog about photography should embed a form with only preset products.',
          'Use the form name to track which embeds perform best. Check orders to see which forms generate the most sales.',
          'If you have a WordPress site, place the form in your sidebar widget area for site-wide visibility.',
        ]
      }
    ]
  },
};

function renderBoldText(text) {
  if (!text || !text.includes('**')) return text;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) => 
    i % 2 === 1 ? <strong key={i} className="font-semibold text-foreground">{part}</strong> : part
  );
}

function SectionRenderer({ section }) {
  if (section.type === 'overview') {
    return (
      <Card className="border-primary/20 bg-accent/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Lightbulb className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm">{section.title}</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{renderBoldText(section.content)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (section.type === 'steps') {
    const Icon = section.icon || ChevronRight;
    return (
      <Card data-testid={`tutorial-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Icon className="h-4.5 w-4.5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base">{section.title}</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{renderBoldText(section.content)}</p>
            </div>
          </div>

          {section.details && section.details.length > 0 && (
            <div className="ml-12 space-y-3">
              {section.details.map((detail, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
                  <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">{String.fromCharCode(65 + i)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{detail.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{detail.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (section.type === 'warning') {
    return (
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-amber-900">{section.title}</p>
              <div className="mt-3 space-y-2.5">
                {section.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (section.type === 'tip') {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Lightbulb className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">{section.title}</p>
              <div className="mt-3 space-y-2.5">
                {section.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}

export default function GuideTutorialPage() {
  const { tutorialId } = useParams();
  const navigate = useNavigate();
  const tutorial = TUTORIALS[tutorialId];

  if (!tutorial) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Tutorial not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/app/getting-started')}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Getting Started
        </Button>
      </div>
    );
  }

  const Icon = tutorial.icon;

  return (
    <div className="space-y-6 max-w-[900px]" data-testid={`tutorial-page-${tutorialId}`}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <button onClick={() => navigate('/app/getting-started')} className="hover:text-foreground transition-colors duration-150">
          Getting Started
        </button>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-foreground font-medium">{tutorial.title}</span>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className={`h-14 w-14 rounded-xl flex items-center justify-center shrink-0 ${tutorial.color}`}>
          <Icon className="h-7 w-7" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">Step {tutorial.number} of 6</Badge>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">{tutorial.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{tutorial.subtitle}</p>
        </div>
      </div>

      <Separator />

      {/* Sections */}
      <div className="space-y-5">
        {tutorial.sections.map((section, i) => (
          <SectionRenderer key={i} section={section} />
        ))}
      </div>

      <Separator />

      {/* Navigation */}
      <div className="flex items-center justify-between py-2">
        <div>
          {tutorial.prev ? (
            <Button variant="outline" onClick={() => navigate(`/app/getting-started/${tutorial.prev}`)} data-testid="tutorial-nav-prev">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Previous
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate('/app/getting-started')} data-testid="tutorial-nav-back">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              All Guides
            </Button>
          )}
        </div>
        <div>
          {tutorial.next ? (
            <Button onClick={() => navigate(`/app/getting-started/${tutorial.next}`)} data-testid="tutorial-nav-next">
              {tutorial.nextLabel}
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={() => navigate('/app/getting-started')} data-testid="tutorial-nav-finish">
              <Rocket className="h-4 w-4 mr-1.5" />
              Back to Getting Started
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
